import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiInstance } from '../services/service-trips-api/service-trips-api';
import {
  CreatePassengerRequest,
  UpdatePassengerDetailsRequest,
  toPassengerId,
} from '../services/service-trips-api/types';
import {
  QueryKeys,
  PassengerListParams,
  PassengerListQueryOptions,
  QueryThrowOnErrorOptions,
  OperationalAttributesQueryOptions,
} from './types';

const buildSearchCriteria = (searchString: string) => {
  const trimmed = searchString.trim();
  const isNumeric = trimmed !== '' && !isNaN(Number(trimmed));
  return isNumeric
    ? { personNoCriteria: { eq: Number(trimmed) } }
    : { freeSearch: { sim: trimmed ? trimmed.split(/\s+/) : [''] } };
};

const normalizeInstitutionIds = (institutionIds?: Array<string | null | undefined>) => {
  return institutionIds
    ?.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    .map((id) => id.trim());
};

export const getPassengerListPayload = ({
  searchString,
  pageNumber,
  itemsPerPage,
  statusFilter,
  agreements,
  institutionIds,
}: PassengerListParams) => {
  const searchCriteria = buildSearchCriteria(searchString);
  const normalizedInstitutionIds = normalizeInstitutionIds(institutionIds);

  return {
    ...searchCriteria,
    pageNumber,
    itemsPerPage,
    ...(statusFilter ? { statusFilter } : {}),
    ...(agreements?.length ? { agreementIds: agreements.map(({ id }) => id) } : {}),
    ...(normalizedInstitutionIds?.length ? { institutionIds: normalizedInstitutionIds } : {}),
  };
};

export const usePassengerListQuery = (
  params: PassengerListParams,
  options?: PassengerListQueryOptions,
) =>
  useQuery(getPassengerListQueryOptions(params, options));

export const getPassengerListQueryOptions = (
  params: PassengerListParams,
  options?: PassengerListQueryOptions,
) => ({
  queryKey: [
    QueryKeys.ListPassengers,
    params.searchString,
    params.pageNumber,
    params.itemsPerPage,
    params.statusFilter,
    params.agreements,
    params.institutionIds,
  ],
  queryFn: async () => {
    const payload = getPassengerListPayload(params);
    return await apiInstance.getPassengerList(payload);
  },
  staleTime: 60_000,
  enabled: options?.enabled ?? true,
});

export const getPassengerDetailsQueryOptions = (passengerId: number) => ({
  queryKey: [QueryKeys.PassengerDetails, passengerId],
  queryFn: () => apiInstance.getPassenger(toPassengerId(passengerId)),
  enabled: !!passengerId,
});

export const usePassengerDetailsQuery = (passengerId: number) =>
  useQuery(getPassengerDetailsQueryOptions(passengerId));

export const usePassengerAdressesQuery = (passengerId: number) =>
  useQuery({
    queryKey: [QueryKeys.PassengerAddress, passengerId],
    queryFn: async () => {
      return apiInstance.getPassengerAddresses(toPassengerId(passengerId));
    },
  });

/** Rows per page for the client-side-paginated assigned-users list (ENB-2452). */
export const ASSIGNED_USERS_PAGE_SIZE = 25;

/**
 * GET /v2/passengers/{id}/assigned-users takes no query params and returns the full list
 * (ENB-2452) — pagination of the result happens client-side in the UI, not via the API.
 */
export const usePassengerAssignedUsersQuery = (
  passengerId: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [QueryKeys.PassengerAssignedUsers, passengerId],
    queryFn: () => apiInstance.getPassengerAssignedUsers(toPassengerId(passengerId)),
    enabled: (options?.enabled ?? true) && !!passengerId,
    staleTime: 60_000,
  });

export const useCreatePassengerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ListPassengers] });
    },
    mutationFn: async (payload: CreatePassengerRequest) => {
      return await apiInstance.createPassenger(payload);
    },
  });
};

export const useEditPassengerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ListPassengers],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PassengerDetails],
      });
    },
    mutationFn: async (payload: {
      id: number;
      data: UpdatePassengerDetailsRequest;
    }) => {
      return await apiInstance.editPassenger(toPassengerId(payload.id), payload.data);
    },
  });
};

const usePassengerReviewMutation = (
  mutationFn: (passengerId: number) => Promise<unknown>,
): UseMutationResult<unknown, Error, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async (_data, passengerId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerDetails] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.ListPassengers] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerPendingReview, passengerId] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SchedulePendingReview] }),
      ]);
    },
  });
};

export const useApprovePassengerMutation = (): UseMutationResult<unknown, Error, number> =>
  usePassengerReviewMutation((passengerId) => apiInstance.approvePassenger(toPassengerId(passengerId)));

export const useRejectPassengerMutation = (): UseMutationResult<unknown, Error, number> =>
  usePassengerReviewMutation((passengerId) => apiInstance.rejectPassenger(toPassengerId(passengerId)));

export const useCompletePassengerMutation = (): UseMutationResult<unknown, Error, number> =>
  usePassengerReviewMutation((passengerId) => apiInstance.completePassenger(toPassengerId(passengerId)));

export const useActivatePassengerMutation = (): UseMutationResult<unknown, Error, number> =>
  usePassengerReviewMutation((passengerId) => apiInstance.activatePassenger(toPassengerId(passengerId)));

const STALE_TIME_1_HOUR = 3600 * 1000;

export const useAttributesQuery = (options?: QueryThrowOnErrorOptions) =>
  useQuery({
    queryKey: [QueryKeys.Attributes],
    queryFn: () => apiInstance.getAttributes('passenger'),
    staleTime: STALE_TIME_1_HOUR,
    throwOnError: options?.throwOnError ?? true,
  });

export const useOperationalAttributesQuery = (options?: OperationalAttributesQueryOptions) =>
  useQuery({
    queryKey: [QueryKeys.OperationalAttributes],
    queryFn: apiInstance.getOperationalAttributes,
    staleTime: STALE_TIME_1_HOUR,
    throwOnError: options?.throwOnError ?? true,
    enabled: options?.enabled ?? true,
  });

export const useAddressTypesQuery = (options?: QueryThrowOnErrorOptions) =>
  useQuery({
    queryKey: [QueryKeys.AddressTypes],
    queryFn: apiInstance.getAddressTypes,
    staleTime: STALE_TIME_1_HOUR,
    throwOnError: options?.throwOnError ?? true,
  });

export const useAddressInstitutionsQuery = (enabled = true) =>
  useQuery({
    queryKey: [QueryKeys.AddressInstitutions],
    queryFn: apiInstance.getAddressInstitutions,
    staleTime: STALE_TIME_1_HOUR,
    enabled,
    throwOnError: true,
  });

export const useContactTypesQuery = (options?: QueryThrowOnErrorOptions) =>
  useQuery({
    queryKey: [QueryKeys.ContactTypes],
    queryFn: apiInstance.getContactTypes,
    staleTime: STALE_TIME_1_HOUR,
    throwOnError: options?.throwOnError ?? true,
  });

export const usePassengerSchedulesQuery = (
  passengerId?: number,
  fromDate?: string,
  toDate?: string
) =>
  useQuery({
    queryKey: [QueryKeys.PassengerSchedules, passengerId, fromDate, toDate],
    queryFn: async () => {
      if (passengerId) {
        const data = await apiInstance.getPassenger(toPassengerId(passengerId));
        return data.schedules ?? [];
      }
      return [];
    },
  });
